import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useCreateBlockNote, SuggestionMenuController, getDefaultReactSlashMenuItems } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { B, radius } from '../../constants/brand';
import { api } from '../../api/client';
import { UserContext } from '../../context/UserContext';
import { useApi } from '../../hooks/useApi';
import { getAccessToken } from '../../config/auth';
import { exportNotesPdf } from '../../utils/exportDownload';
import { Btn, Spinner, useToast, Icon, Chip, Modal, ModalFooter } from '../ui';
import { InkCanvas } from './InkCanvas';
import { DealNoteComposer, pendingComposerNoteId, composerScopeKey } from './DealNoteComposer';
import { makeNotebookFileResolver } from '../../notebook/files';
import { FixedScopeProjectPicker } from './DealScopePicker';
import { useTaskCreate } from '../tasks/useTaskCreate';
import { TaskDraftModal } from './TaskDraftModal';
import { notifyTasksChanged } from '../../utils/taskEvents';
import { prepareFileForOcr, isPdfFile, ocrFileNoun, runHandwritingOcr } from '../../utils/handwritingOcr';
import { formatInstant, todayIsoInZone } from '../../utils/format';

// A note written in the Notes app can hold images and files inline; their
// blocks store a stable reference that has to be turned into a link to render.
const resolveFileUrl = makeNotebookFileResolver(api);


// Today in the person's saved zone — the UTC date is already tomorrow by evening.
export const todayISO = () => todayIsoInZone();

const fmtNoteDate = (utcStr) => {
  if (!utcStr) return '';
  return formatInstant(utcStr);
};

const fmtUserDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const downloadNoteAttachment = async (noteId, attachmentId, fileName, onError) => {
  try {
    const token = await getAccessToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const r = await fetch(`/api/notes/${noteId}/attachments/${attachmentId}`, { headers });
    if (!r.ok) { if (onError) onError('Download failed'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  } catch { if (onError) onError('Download failed'); }
};



// Detect whether stored content is BlockNote JSON or legacy HTML
const isBlockNoteJSON = (content) => {
  if (!content) return false;
  const trimmed = content.trim();
  return trimmed.startsWith('[') && trimmed.endsWith(']');
};

// BlockNote theme matching brand colors
const BN_THEME = {
  light: {
    colors: {
      editor: { text: '#1a2a32', background: '#ffffff' },
      menu: { text: '#1a2a32', background: '#ffffff' },
      tooltip: { text: '#ffffff', background: '#234c5a' },
      hovered: { text: '#1a2a32', background: '#eef5f4' },
      selected: { text: '#234c5a', background: '#d4ece8' },
      disabled: { text: '#7a9aaa', background: '#f5f7f6' },
      shadow: 'rgba(0,0,0,0.12)',
      border: '#dde8e5',
      sideMenu: '#7a9aaa',
      highlights: {
        gray: { text: '#6b7280', background: '#f3f4f6' },
        brown: { text: '#92400e', background: '#fef3c7' },
        red: { text: '#b91c1c', background: '#fee2e2' },
        orange: { text: '#c2410c', background: '#ffedd5' },
        yellow: { text: '#a16207', background: '#fef9c3' },
        green: { text: '#15803d', background: '#dcfce7' },
        blue: { text: '#1d4ed8', background: '#dbeafe' },
        purple: { text: '#7e22ce', background: '#f3e8ff' },
        pink: { text: '#be185d', background: '#fce7f3' },
      },
    },
    borderRadius: 6,
    fontFamily: 'inherit',
  },
};

// ─── Rich note editor (BlockNote) ────────────────────────────────────────────

// `dealId`/`dealDetailId` are ONLY passed by the compose (new-note) call site
// in NotesFeed, deliberately not by NoteCard's edit-mode instance: PUT
// /api/notes/{id} does not persist ProjectID (only the POST create path and
// the getNotes filter do — see notes.js), so offering the picker on an edit
// would silently do nothing. Present them and the project picker appears;
// omit them (a task-scoped composer with no deal in its own props, or an edit)
// and it stays exactly as it was before Projects existed.
export const RichNoteEditor = ({ users, initialContent, initialMarkdown, initialNoteDate, initialAuthorUserId, dealId, dealDetailId, onSubmit, onCancel, onContentChange }) => {
  const { toast } = useToast();
  const currentUser = useContext(UserContext);
  const fileInputRef = useRef();
  const cameraInputRef = useRef();
  const uploadScanInputRef = useRef();
  const attachKeyRef = useRef(0);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [inkOpen, setInkOpen] = useState(false);
  const [noteDate, setNoteDate] = useState(initialNoteDate ? initialNoteDate.slice(0, 10) : todayISO());
  // ORTHOGONAL TAG (see ProjectPicker.jsx / dealProjects.js) — which of this
  // scope's Projects the note belongs to, or none. Only offered when the
  // caller supplied a dealId (see the component comment above).
  const [projectId, setProjectId] = useState('');

  // "Entered on behalf of" author picker. The person filling in the editor is
  // always the enterer server-side; this lets them attribute the note to the
  // colleague who actually took it. Defaults to the current user.
  const internalUsers = useMemo(
    () => (users || []).filter(u => u.isInternal && u.isActive !== false),
    [users],
  );
  const meUserId = useMemo(
    () => internalUsers.find(u => u.email === currentUser?.email)?.userID || '',
    [internalUsers, currentUser],
  );
  const [authorUserId, setAuthorUserId] = useState(initialAuthorUserId || '');
  // Once the user list resolves, default the picker to "me" (unless we're
  // editing a note that already carries an explicit author).
  useEffect(() => {
    if (!authorUserId && !initialAuthorUserId && meUserId) setAuthorUserId(meUserId);
  }, [meUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isJSON = isBlockNoteJSON(initialContent);

  const initialBlocks = useMemo(() => {
    if (isJSON) {
      try { return JSON.parse(initialContent); } catch { return undefined; }
    }
    return undefined;
  }, []);

  const editor = useCreateBlockNote({ initialContent: initialBlocks, resolveFileUrl });

  // After the editor is ready, prefill it from Markdown (a Claude-generated
  // combine-notes draft) or from a legacy HTML note. JSON notes are already
  // loaded via initialContent above.
  useEffect(() => {
    if (!editor) return;
    if (initialMarkdown && !initialContent) {
      (async () => {
        try {
          const blocks = await editor.tryParseMarkdownToBlocks(initialMarkdown);
          if (blocks?.length) editor.replaceBlocks(editor.document, blocks);
        } catch { /* leave editor empty so the user can type manually */ }
      })();
      return;
    }
    if (isJSON || !initialContent) return;
    const blocks = editor.tryParseHTMLToBlocks(initialContent);
    if (blocks?.length) editor.replaceBlocks(editor.document, blocks);
  }, [editor]);

  // iOS Safari freeze fix: posting/cancelling closes this composer, which
  // unmounts the focused BlockNote (contentEditable) editor. On iOS, tearing
  // down a focused contentEditable while the soft keyboard is open leaves the
  // page stuck — the visual viewport stays offset for the vanished keyboard and
  // neither taps nor scrolls register, so the screen appears frozen. Blurring
  // the active element first (while still inside the tap gesture, so iOS honors
  // it) collapses the keyboard and restores the viewport before the unmount.
  const dismissKeyboard = () => {
    if (typeof document !== 'undefined') document.activeElement?.blur?.();
  };

  // Report the live content to the parent for draft autosave (debounced there).
  const reportChange = () => {
    if (!editor || !onContentChange) return;
    onContentChange({ content: JSON.stringify(editor.document), noteDate: noteDate || null });
  };

  const handleSubmit = async () => {
    if (!editor) return;
    const blocks = editor.document;
    const isEmpty = blocks.length === 0 || (blocks.length === 1 && blocks[0].content?.length === 0);
    if (isEmpty) return;
    dismissKeyboard();
    setSaving(true);
    try {
      const content = JSON.stringify(blocks);
      // Extract any mentioned user IDs from inline content
      const mentionedUserIDs = [];
      await onSubmit({
        content, mentionedUserIDs, attachments, noteDate: noteDate || null,
        authorUserId: authorUserId || null, projectId: projectId || null,
      });
      editor.replaceBlocks(editor.document, []);
      setAttachments([]);
      setProjectId('');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ev => resolve(ev.target.result.split(',')[1]);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

  // Upload a picked file's bytes STRAIGHT to Blob Storage via a short-lived SAS
  // write URL, so a multi-MB file (e.g. a PDF) never travels inside the note's
  // JSON request body — which is what made large attachments fail. Returns a
  // { blobContainer, blobPath } reference, or null when the server says direct
  // upload isn't available (no storage account / pre-migration schema) so the
  // caller falls back to inline base64.
  const uploadAttachmentToBlob = async (file) => {
    const token   = await getAccessToken();
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    const r = await fetch('/api/notes/attachment-upload-url', {
      method: 'POST', headers,
      body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream' }),
    });
    if (!r.ok) throw new Error('Could not start upload');
    const info = await r.json();
    if (!info.available) return null;  // caller falls back to base64
    const put = await fetch(info.sasUrl, {
      method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!put.ok) throw new Error('Upload failed');
    return { blobContainer: info.blobContainer, blobPath: info.blobPath };
  };

  const handleFileAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      const key = `att-${attachKeyRef.current++}`;
      // Show the chip immediately in an "uploading" state.
      setAttachments(prev => [...prev, {
        key, fileName: file.name, fileSizeBytes: file.size,
        contentType: file.type || 'application/octet-stream', uploading: true,
      }]);
      try {
        let ref = null;
        try { ref = await uploadAttachmentToBlob(file); }
        catch { ref = null; }  // network/storage hiccup — fall back to inline base64
        const patch = ref
          ? { ...ref, uploading: false }
          : { blobData: await readFileAsBase64(file), uploading: false };
        setAttachments(prev => prev.map(a => (a.key === key ? { ...a, ...patch } : a)));
      } catch (err) {
        toast.error(`Couldn't attach ${file.name}`);
        setAttachments(prev => prev.filter(a => a.key !== key));
      }
    }
  };

  // Append blocks to the editor, replacing the document if it's still empty so
  // we don't leave a stray blank line on top.
  const insertBlocksAtEnd = (blocks) => {
    if (!editor || !blocks?.length) return;
    const docBlocks = editor.document;
    const last = docBlocks[docBlocks.length - 1];
    const onlyEmpty = docBlocks.length === 1 && (last?.content?.length ?? 0) === 0;
    if (onlyEmpty) editor.replaceBlocks(docBlocks, blocks);
    else editor.insertBlocks(blocks, last, 'after');
  };

  // Plain fallback: one paragraph per line.
  const insertExtractedText = (text) => {
    if (!text?.trim()) return;
    insertBlocksAtEnd(text.split('\n').map(line => ({ type: 'paragraph', content: line || '' })));
  };

  // Shared pipeline: send an image (photo or pencil ink) to the OCR/Claude
  // endpoint, insert the formatted text, and keep the image as an attachment.
  // Returns true if any text was inserted.
  //
  // `attachment` overrides how the source file is kept on the note: the inline
  // base64 default is right for a canvas-encoded JPEG, and wrong for a PDF up
  // to 20 MB — that has to ride the direct-to-blob path the Attach button uses,
  // or the note's own JSON body carries the whole file and fails at the
  // platform limit (the reason uploadAttachmentToBlob exists).
  const processImageForOcr = async ({ base64, contentType, sizeBytes, fileName, attachment }) => {
    const noun = contentType === 'application/pdf' ? 'PDF' : 'image';
    setScanning(true);
    try {
      // Fire-and-poll: a long PDF outlives the edge's ~45 s, so the request's
      // response is not the outcome — the job row is (utils/handwritingOcr.js).
      const { text, markdown } = await runHandwritingOcr({ base64, contentType, getAccessToken });
      // Prefer the Claude-formatted Markdown (clean outline); fall back to the
      // raw OCR text as plain paragraphs if formatting is missing or unparseable.
      let inserted = false;
      if (markdown?.trim()) {
        try {
          const blocks = await editor.tryParseMarkdownToBlocks(markdown);
          if (blocks?.length) { insertBlocksAtEnd(blocks); inserted = true; }
        } catch { /* fall back to raw text */ }
      }
      if (!inserted && text?.trim()) { insertExtractedText(text); inserted = true; }
      if (inserted) {
        toast.success('Text extracted — review and edit before posting');
      } else {
        toast.info(`No text found in the ${noun}`);
      }
      // Keep the source file on the note via the existing attachment flow
      setAttachments(prev => [...prev, attachment || { fileName, fileSizeBytes: sizeBytes, contentType, blobData: base64 }]);
      return inserted;
    } catch (err) {
      toast.error(err.message || 'Text extraction failed');
      return false;
    } finally {
      setScanning(false);
    }
  };

  // Capture a photo of handwritten notes — or upload a photo or a scanned PDF
  // of one — and run it through the pipeline.
  const handleScan = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    let prepared;
    try {
      // A photo is downscaled + JPEG-encoded (handles large HEIC photos); a PDF
      // is read whole under its own content type — see utils/handwritingOcr.js.
      prepared = await prepareFileForOcr(file);
    } catch (err) {
      toast.error(err.message || `Could not read ${ocrFileNoun(file)}`);
      return;
    }
    const baseName = (file.name || 'scan').replace(/\.[^.]+$/, '');
    if (!isPdfFile(file)) {
      await processImageForOcr({ ...prepared, fileName: `${baseName}.jpg` });
      return;
    }
    // A PDF keeps its own name and bytes, uploaded straight to Blob Storage
    // (inline base64 only as the fallback the Attach button also takes).
    const fileName = /\.pdf$/i.test(file.name || '') ? file.name : `${baseName}.pdf`;
    let ref = null;
    try { ref = await uploadAttachmentToBlob(file); } catch { ref = null; }
    const attachment = {
      fileName, fileSizeBytes: file.size, contentType: 'application/pdf',
      ...(ref ? ref : { blobData: prepared.base64 }),
    };
    await processImageForOcr({ ...prepared, fileName, attachment });
  };

  // Pencil ink → text: run the drawing through the same OCR/Claude pipeline.
  const handleInkConvert = async ({ base64, sizeBytes }) => {
    const ok = await processImageForOcr({ base64, contentType: 'image/jpeg', sizeBytes, fileName: 'handwriting.jpg' });
    if (ok) setInkOpen(false);
  };

  // Pencil ink → keep as a drawing attachment, no conversion.
  const handleInkKeep = ({ base64, sizeBytes }) => {
    setAttachments(prev => [...prev, {
      fileName: 'handwriting.jpg', fileSizeBytes: sizeBytes, contentType: 'image/jpeg', blobData: base64,
    }]);
    toast.success('Drawing added to note');
    setInkOpen(false);
  };

  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, background: B.white, marginBottom: 20, width: '100%', boxSizing: 'border-box' }}>
      <BlockNoteView
        editor={editor}
        theme={BN_THEME}
        slashMenu={false}
        onChange={reportChange}
        onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleSubmit(); }}
        style={{ minHeight: 120, width: '100%' }}
      >
        <SuggestionMenuController
          triggerCharacter="/"
          getItems={async query => getDefaultReactSlashMenuItems(editor).filter(
            item => !query || item.title.toLowerCase().includes(query.toLowerCase())
          )}
          // BlockNote floats the slash menu at a low default z-index (~70), which
          // leaves it *under* the app modal (overlay z-index 1000) — so inside a
          // task/detail modal the "/" menu was hidden behind the dialog. Raise its
          // floating wrapper above app chrome, matching the mantine-dropdown rule
          // in the <style> block below.
          floatingUIOptions={{
            useFloatingOptions: { strategy: 'fixed' },
            elementProps: { style: { zIndex: 9999 } },
          }}
        />
      </BlockNoteView>
      {attachments.length > 0 && (
        <div style={{ padding: '6px 12px', borderTop: `1px solid ${B.border}`, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {attachments.map((a, i) => (
            <div key={a.key ?? i} style={{ display: 'flex', alignItems: 'center', gap: 5, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 20, padding: '3px 10px', fontSize: 12, opacity: a.uploading ? 0.6 : 1 }}>
              <Icon name="attach" size={12} /> {a.fileName}
              {a.uploading && <span style={{ color: B.textLt }}>uploading…</span>}
              <button onClick={() => setAttachments(p => p.filter((_, j) => j !== i))}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: B.textLt, display: 'inline-flex', padding: '0 0 0 4px', lineHeight: 1 }}><Icon name="close" size={13} /></button>
            </div>
          ))}
        </div>
      )}
      {dealId && (
        <div style={{ padding: '2px 12px 0', borderTop: `1px solid ${B.border}` }}>
          <FixedScopeProjectPicker dealId={dealId} dealDetailId={dealDetailId} value={projectId} onChange={setProjectId} />
        </div>
      )}
      {inkOpen && (
        <InkCanvas
          busy={scanning}
          onConvert={handleInkConvert}
          onKeepDrawing={handleInkKeep}
          onClose={() => setInkOpen(false)}
        />
      )}
      <div style={{ display: 'flex', gap: 8, padding: '8px 12px', alignItems: 'center', borderTop: `1px solid ${B.border}`, flexWrap: 'wrap' }}>
        <Btn variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}><Icon name="attach" size={14} /> Attach</Btn>
        <input ref={fileInputRef} type="file" hidden multiple onChange={handleFileAttach} />
        <Btn variant="ghost" size="sm" disabled={scanning} onClick={() => cameraInputRef.current?.click()}>
          <Icon name="camera" size={14} /> {scanning ? 'Scanning…' : 'Scan'}
        </Btn>
        <input ref={cameraInputRef} type="file" hidden accept="image/*" capture="environment" onChange={handleScan} />
        <Btn variant="ghost" size="sm" disabled={scanning} onClick={() => uploadScanInputRef.current?.click()}><Icon name="image" size={14} /> Scan image or PDF</Btn>
        <input ref={uploadScanInputRef} type="file" hidden accept="image/*,application/pdf,.pdf" onChange={handleScan} />
        <Btn variant="ghost" size="sm" disabled={scanning} onClick={() => setInkOpen(o => !o)}><Icon name="pen" size={14} /> Write</Btn>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: B.textMd }}>
          Date
          <input type="date" value={noteDate}
            onChange={e => { setNoteDate(e.target.value); if (editor && onContentChange) onContentChange({ content: JSON.stringify(editor.document), noteDate: e.target.value || null }); }}
            style={{ fontSize: 12, color: B.text, border: `1px solid ${B.border}`, borderRadius: 4, padding: '2px 6px', fontFamily: 'inherit', background: B.white }} />
        </label>
        {internalUsers.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: B.textMd }} title="Attribute this note to the person who took it">
            Author
            <select value={authorUserId}
              onChange={e => setAuthorUserId(e.target.value)}
              style={{ fontSize: 12, color: B.text, border: `1px solid ${B.border}`, borderRadius: 4, padding: '2px 6px', fontFamily: 'inherit', background: B.white, maxWidth: 160 }}>
              {!meUserId && <option value="">Me</option>}
              {internalUsers.map(u => (
                <option key={u.userID} value={u.userID}>
                  {u.displayName}{u.userID === meUserId ? ' (me)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        <div style={{ flex: 1 }} />
        {onCancel && <Btn variant="ghost" size="sm" onClick={() => { dismissKeyboard(); onCancel(); }}>Cancel</Btn>}
        <Btn size="sm" disabled={saving || attachments.some(a => a.uploading)} onClick={handleSubmit}>{saving ? 'Posting…' : 'Post'}</Btn>
      </div>
    </div>
  );
};

// ─── Read-only BlockNote view (for saved JSON notes) ─────────────────────────

const NoteReadView = ({ content, onCheckToggle, onPromoteToTask }) => {
  const wrapperRef = useRef();
  const [hoverItem, setHoverItem] = useState(null);

  const blocks = useMemo(() => {
    try { return JSON.parse(content); } catch { return []; }
  }, [content]);

  const editor = useCreateBlockNote({ initialContent: blocks, resolveFileUrl });

  const handleClick = (e) => {
    if (e.target.type !== 'checkbox' || !onCheckToggle) return;
    e.stopPropagation();
    const blockId = e.target.closest('[data-id]')?.dataset.id;
    if (blockId) {
      editor.updateBlock(blockId, { props: { checked: e.target.checked } });
      onCheckToggle(JSON.stringify(editor.document));
    }
  };

  const handleMouseOver = (e) => {
    const blockEl = e.target.closest('[data-content-type="checkListItem"]') ||
                    e.target.closest('.bn-block-content[data-content-type="checkListItem"]');
    if (blockEl && onPromoteToTask) {
      const textEl = blockEl.querySelector('.bn-inline-content');
      const text = (textEl || blockEl).textContent?.trim();
      if (text) setHoverItem(text);
    } else {
      setHoverItem(null);
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', fontSize: 13, lineHeight: 1.7, color: B.text }}
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseLeave={() => setHoverItem(null)}
    >
      <BlockNoteView editor={editor} editable={false} theme={BN_THEME} />
      {hoverItem && onPromoteToTask && (
        <button
          onClick={e => { e.stopPropagation(); onPromoteToTask({ text: hoverItem }); }}
          style={{
            position: 'absolute', top: 0, right: 0,
            fontSize: 11, padding: '2px 8px', borderRadius: 4,
            border: `1px solid ${B.tealDk}`, background: B.tealLt,
            color: B.navy, cursor: 'pointer', fontWeight: 600,
            fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}
        ><Icon name="arrowRight" size={13} /> Task</button>
      )}
    </div>
  );
};

// ─── Note card (view + inline edit) ──────────────────────────────────────────

// Cross-deal tags shown on a note card: solid removable chips plus an inline
// "add tag" input with autocomplete off GET /api/note-tags. Keeps its own copy
// of the tag list (seeded from the note) so add/remove reflect immediately
// without a full feed reload.
const NoteTagChips = ({ noteId, tags, canEdit, onChange }) => {
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!adding) { setSuggestions([]); return undefined; }
    const q = input.trim();
    const t = setTimeout(async () => {
      try { const r = await api.get(`note-tags?q=${encodeURIComponent(q)}`); setSuggestions(r.tags || []); }
      catch { setSuggestions([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [input, adding]);

  const apply = async (label) => {
    const t = (label || '').trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const r = await api.put(`notes/${noteId}/tags`, { apply: [t] });
      onChange?.(r.tags || []);
      setInput(''); setAdding(false); setSuggestions([]);
    } catch (e) { toast.error(e.message || 'Could not add tag'); }
    finally { setBusy(false); }
  };
  const remove = async (tag) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.put(`notes/${noteId}/tags`, { remove: [tag] });
      onChange?.(r.tags || []);
    } catch (e) { toast.error(e.message || 'Could not remove tag'); }
    finally { setBusy(false); }
  };

  if (!(tags || []).length && !canEdit) return null;
  return (
    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginTop: 10 }}>
      {(tags || []).map(t => (
        <span key={t.tag} title={`#${t.label}`}>
          <Chip color={B.navy} onRemove={canEdit ? () => remove(t.tag) : undefined}
            style={{ fontSize: 11, padding: '2px 4px 2px 9px' }}>
            #{t.label}
          </Chip>
        </span>
      ))}
      {canEdit && !adding && (
        <button onClick={(e) => { e.stopPropagation(); setAdding(true); }} title="Add a tag"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none',
            border: `1px dashed ${B.border}`, borderRadius: radius.pill, padding: '2px 9px',
            fontSize: 11, fontWeight: 600, color: B.textMd, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Icon name={(tags || []).length ? 'add' : 'add'} size={11} /> Tag
        </button>
      )}
      {canEdit && adding && (
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <input
            autoFocus
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); apply(input); }
              else if (e.key === 'Escape') { setAdding(false); setInput(''); }
            }}
            onBlur={() => setTimeout(() => setAdding(false), 150)}
            placeholder="tag…"
            style={{ fontSize: 11, padding: '3px 8px', border: `1px solid ${B.navy}`, borderRadius: radius.pill,
              outline: 'none', fontFamily: 'inherit', width: 120 }}
          />
          {suggestions.length > 0 && (
            <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, zIndex: 20, minWidth: 140,
              background: B.white, border: `1px solid ${B.border}`, borderRadius: 8, boxShadow: B.shadowLg, overflow: 'hidden' }}>
              {suggestions.slice(0, 8).map(s => (
                <button key={s.tag} onMouseDown={(e) => { e.preventDefault(); apply(s.label); }}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 8, width: '100%', textAlign: 'left',
                    padding: '6px 10px', border: 'none', borderBottom: `1px solid ${B.border}`, background: B.white,
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: B.text }}>
                  <span>#{s.label}</span><span style={{ color: B.textLt }}>{s.count}</span>
                </button>
              ))}
            </div>
          )}
        </span>
      )}
    </div>
  );
};

// Version-history viewer: lists prior-content snapshots (newest first) with a
// read-only preview of the selected one and, for editors, a Restore action.
const NoteHistoryModal = ({ noteId, canRestore, onRestore, onClose }) => {
  const { toast } = useToast();
  const [revs, setRevs] = useState(null);
  const [sel, setSel]   = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try { const r = await api.get(`notes/${noteId}/revisions`); setRevs(r || []); if (r?.length) setSel(r[0]); }
      catch { setRevs([]); }
    })();
  }, [noteId]);
  const restore = async () => {
    if (!sel || busy) return;
    setBusy(true);
    try { await onRestore(sel); onClose(); }
    catch (e) { toast.error(e.message || 'Could not restore this version'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Note history" onClose={onClose} width={760}>
      {revs === null ? <Spinner /> : revs.length === 0 ? (
        <div style={{ padding: '16px 4px', color: B.textMd, fontSize: 13 }}>
          No earlier versions yet. A snapshot is saved automatically each time this note is edited.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, minHeight: 220 }}>
          <div style={{ width: 210, flexShrink: 0, borderRight: `1px solid ${B.border}`, paddingRight: 8, maxHeight: 380, overflowY: 'auto' }}>
            {revs.map(r => (
              <button key={r.revisionID} onClick={() => setSel(r)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: 4,
                  border: `1px solid ${sel?.revisionID === r.revisionID ? B.navy : B.border}`, borderRadius: 8,
                  background: sel?.revisionID === r.revisionID ? B.tealLt : B.white, cursor: 'pointer', fontFamily: 'inherit' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: B.text }}>Version {r.revisionNum}</div>
                <div style={{ fontSize: 11, color: B.textLt, marginTop: 2 }}>
                  {fmtNoteDate(r.createdUTC)}{(r.modifiedByDisplayName || r.modifiedBy) ? ` · ${r.modifiedByDisplayName || r.modifiedBy}` : ''}
                </div>
              </button>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 0, maxHeight: 380, overflowY: 'auto' }}>
            {sel ? <NoteReadView content={sel.content} /> : null}
          </div>
        </div>
      )}
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Close</Btn>
        {canRestore && sel && revs?.length > 0 && (
          <Btn onClick={restore} loading={busy}>Restore this version</Btn>
        )}
      </ModalFooter>
    </Modal>
  );
};

export const NoteCard = ({ note, users, onEdit, onDelete, onPromoteToTask, onSuggestTasks, readOnly = false, highlighted = false }) => {
  const currentUser = useContext(UserContext);
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [hoverCheckItem, setHoverCheckItem] = useState(null);
  const [tags, setTags] = useState(note.tags || []);
  const [showHistory, setShowHistory] = useState(false);
  useEffect(() => { setTags(note.tags || []); }, [note.tags]);
  const contentRef = useRef();

  // Lazily load the original notes that were rolled into this combined note.
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [sources, setSources] = useState(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const toggleSources = async () => {
    if (sourcesOpen) { setSourcesOpen(false); return; }
    setSourcesOpen(true);
    if (sources === null && !sourcesLoading) {
      setSourcesLoading(true);
      try { setSources((await api.get(`notes?supersededBy=${note.noteID}`)) || []); }
      catch { setSources([]); }
      finally { setSourcesLoading(false); }
    }
  };

  // Stop tracking an email thread: this note IS the thread's rolling note, so
  // find the thread by matching NoteID, then stop it. Admin-only endpoint.
  const stopTracking = async () => {
    if (!window.confirm('Stop tracking this email thread? New replies will no longer be pulled in automatically.')) return;
    try {
      const threads = await api.get('email-threads');
      const t = (threads || []).find(x => x.noteID === note.noteID);
      if (!t) { toast.info('This thread is no longer being tracked.'); return; }
      await api.post(`email-threads/${t.threadID}/stop`, {});
      toast.success('Thread tracking stopped');
    } catch (e) { toast.error(e.message || 'Could not stop tracking'); }
  };

  // The note is attributed to its author (the person who took it) when one was
  // set; otherwise to the enterer. When they differ we also show "entered by".
  const authorName = note.authoredByDisplayName || note.createdByDisplayName;
  const enteredOnBehalf = !!note.authoredByDisplayName;
  const initials = (authorName || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const isEdited = note.modifiedUTC && note.modifiedUTC !== note.createdUTC;
  const isOwn = currentUser && (note.createdBy === currentUser.email);
  // readOnly (e.g. the task modal's Deal Notes tab) hides every mutation
  // affordance — edit, delete, click-to-edit, checkbox toggling, promote.
  const canEdit = !readOnly && (isOwn || currentUser?.role === 'Admin');
  const useBlockNote = isBlockNoteJSON(note.content);

  return (
    <div
      id={`note-${note.noteID}`}
      style={{
        display: 'flex', gap: 12, padding: '16px 12px', borderBottom: `1px solid ${B.border}`,
        // Brief highlight when reached via a global-search deep-link.
        background: highlighted ? B.tealLt : 'transparent',
        borderRadius: highlighted ? 8 : 0,
        transition: 'background 0.6s ease',
      }}
    >
      <div style={{ width: 34, height: 34, borderRadius: '50%', background: B.navy, color: B.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>
        {initials}
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 8 }}>
        <div
          onClick={canEdit && !editing && !useBlockNote ? () => setEditing(true) : undefined}
          style={{ flex: 1, minWidth: 0, cursor: canEdit && !editing && !useBlockNote ? 'pointer' : 'default' }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: B.text }}>{authorName}</span>
            {enteredOnBehalf && (
              <span style={{ fontSize: 11, color: B.textLt }} title={`Entered by ${note.createdByDisplayName}`}>
                · entered by {note.createdByDisplayName}
              </span>
            )}
            {note.isCombined && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: B.navy, background: B.tealLt, border: `1px solid ${B.tealDk}`, borderRadius: 10, padding: '1px 8px', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                <Icon name="sparkles" size={11} /> Combined
              </span>
            )}
            {note.sourceType === 'email' && note.sourceEmailConversationId && (
              <span
                title={note.sourceEmailSubject ? `Email thread: ${note.sourceEmailSubject}` : 'Rolling note tracking an email thread'}
                style={{ fontSize: 10, fontWeight: 700, color: B.navy, background: B.tealLt, border: `1px solid ${B.tealDk}`, borderRadius: 10, padding: '1px 8px', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                📧 Email thread
              </span>
            )}
            {note.sourceType === 'email' && note.sourceEmailConversationId && !readOnly && currentUser?.role === 'Admin' && (
              <button onClick={stopTracking}
                title="Stop pulling new replies in this thread"
                style={{ fontSize: 11, color: B.textLt, border: 'none', background: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontFamily: 'inherit' }}>
                Stop tracking
              </button>
            )}
            {/* The one place a note's Project tag (an ORTHOGONAL label, see
                ProjectPicker.jsx) is visible anywhere in the app outside of
                Harvi's own answers and the AI summary — set once at creation,
                never editable here, so this is display-only. */}
            {note.projectName && (
              <span
                title={`Tagged to the "${note.projectName}" project`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: B.navy, background: B.tealLt, border: `1px solid ${B.tealDk}`, borderRadius: 10, padding: '1px 8px' }}>
                📁 {note.projectName}
              </span>
            )}
            <span style={{ fontSize: 11, color: B.textLt }}>
              {note.noteDate ? fmtUserDate(note.noteDate) : fmtNoteDate(note.createdUTC)}
            </span>
            {note.noteDate && <span style={{ fontSize: 11, color: B.textLt }}>· filed {fmtNoteDate(note.createdUTC)}</span>}
            {isEdited && <span style={{ fontSize: 11, color: B.textLt, fontStyle: 'italic' }}>edited</span>}
            {canEdit && !editing && useBlockNote && (
              <button onClick={() => setEditing(true)}
                style={{ fontSize: 11, color: B.textLt, border: 'none', background: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontFamily: 'inherit' }}>Edit</button>
            )}
          </div>

          {editing ? (
            <RichNoteEditor
              users={users}
              initialContent={note.content}
              initialNoteDate={note.noteDate}
              initialAuthorUserId={note.authorUserID || ''}
              onSubmit={async (data) => { await onEdit(note.noteID, data); setEditing(false); }}
              onCancel={() => setEditing(false)}
            />
          ) : useBlockNote ? (
            <NoteReadView
              content={note.content}
              onCheckToggle={readOnly ? undefined : (updated) => onEdit(note.noteID, { content: updated, noteDate: note.noteDate, mentionedUserIDs: [], attachments: [] })}
              onPromoteToTask={onPromoteToTask ? (item) => onPromoteToTask({ ...item, noteId: note.noteID }) : null}
            />
          ) : (
            /* Legacy HTML notes */
            <div
              style={{ position: 'relative' }}
              onMouseLeave={() => setHoverCheckItem(null)}
            >
              <div
                ref={contentRef}
                className="note-content"
                dangerouslySetInnerHTML={{ __html: note.content }}
                style={{ fontSize: 13, lineHeight: 1.7, color: B.text, wordBreak: 'break-word' }}
                onClick={e => {
                  if (readOnly) return;
                  if (e.target.classList?.contains('note-checkbox')) {
                    e.stopPropagation();
                    const checked = e.target.dataset.checked === 'true';
                    e.target.dataset.checked = String(!checked);
                    onEdit(note.noteID, { content: contentRef.current.innerHTML, noteDate: note.noteDate, mentionedUserIDs: [], attachments: [] });
                    return;
                  }
                  if (e.target.type === 'checkbox') {
                    e.stopPropagation();
                    const li = e.target.closest('li[data-type="taskItem"]');
                    if (li) {
                      li.dataset.checked = String(e.target.checked);
                      onEdit(note.noteID, { content: contentRef.current.innerHTML, noteDate: note.noteDate, mentionedUserIDs: [], attachments: [] });
                    }
                    return;
                  }
                }}
                onMouseOver={e => {
                  const legacyItem = e.target.closest?.('.note-check-item');
                  const tiptapItem = e.target.closest?.('li[data-type="taskItem"]');
                  const item = legacyItem || tiptapItem;
                  if (item) {
                    let text = '';
                    if (legacyItem) {
                      text = [...item.childNodes]
                        .filter(n => !(n.nodeType === Node.ELEMENT_NODE && n.classList.contains('note-checkbox')))
                        .map(n => n.textContent).join('').trim();
                    } else {
                      const textEl = item.querySelector(':scope > div, :scope > p');
                      text = (textEl || item).textContent.trim();
                    }
                    if (text) setHoverCheckItem(text);
                  } else {
                    setHoverCheckItem(null);
                  }
                }}
              />
              {hoverCheckItem && onPromoteToTask && (
                <button
                  onClick={e => { e.stopPropagation(); onPromoteToTask({ text: hoverCheckItem, noteId: note.noteID }); }}
                  style={{
                    position: 'absolute', top: 0, right: 0,
                    fontSize: 11, padding: '2px 8px', borderRadius: 4,
                    border: `1px solid ${B.tealDk}`, background: B.tealLt,
                    color: B.navy, cursor: 'pointer', fontWeight: 600,
                    fontFamily: 'inherit', whiteSpace: 'nowrap',
                  }}
                ><Icon name="arrowRight" size={13} /> Task</button>
              )}
            </div>
          )}

          {note.attachments?.length > 0 && (
            <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              {note.attachments.map(a => (
                <div key={a.attachmentID}>
                  <button
                    onClick={() => downloadNoteAttachment(note.noteID, a.attachmentID, a.fileName, toast.error)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 20, padding: '3px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: B.text }}>
                    <Icon name="attach" size={12} /> {a.fileName}
                    {a.fileSizeBytes && <span style={{ color: B.textLt }}>({(a.fileSizeBytes / 1024).toFixed(0)} KB)</span>}
                  </button>
                  {a.aiSummary ? (
                    <div style={{ marginTop: 5, padding: '7px 11px', background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 8, fontSize: 12, color: B.textMd, lineHeight: 1.5 }}>
                      <div style={{ fontWeight: 700, color: B.textLt, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>AI summary</div>
                      <div className="note-content" dangerouslySetInnerHTML={{ __html: a.aiSummary }} />
                    </div>
                  ) : a.aiSummaryStatus === 'pending' ? (
                    <div style={{ marginTop: 4, fontSize: 11, color: B.textLt, fontStyle: 'italic' }}>Summarizing document…</div>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          <NoteTagChips noteId={note.noteID} tags={tags} canEdit={canEdit} onChange={setTags} />

          {note.supersededCount > 0 && (
            <div onClick={e => e.stopPropagation()} style={{ marginTop: 10 }}>
              <button onClick={toggleSources}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 11, color: B.textMd, fontWeight: 600 }}>
                <Icon name={sourcesOpen ? 'chevronDown' : 'chevronRight'} size={12} /> Combined from {note.supersededCount} original note{note.supersededCount > 1 ? 's' : ''}
              </button>
              {sourcesOpen && (
                <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: `2px solid ${B.border}` }}>
                  {sourcesLoading && <Spinner />}
                  {(sources || []).map(s => (
                    <div key={s.noteID} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: B.textLt, marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, color: B.textMd }}>{s.authoredByDisplayName || s.createdByDisplayName}</span>
                        {' · '}{s.noteDate ? fmtUserDate(s.noteDate) : fmtNoteDate(s.createdUTC)}
                      </div>
                      {isBlockNoteJSON(s.content)
                        ? <NoteReadView content={s.content} />
                        : <div className="note-content" dangerouslySetInnerHTML={{ __html: s.content }}
                            style={{ fontSize: 13, lineHeight: 1.7, color: B.text, wordBreak: 'break-word' }} />}
                    </div>
                  ))}
                  {!sourcesLoading && (sources || []).length === 0 && (
                    <div style={{ fontSize: 12, color: B.textLt }}>Original notes are no longer available.</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {((canEdit || onSuggestTasks) && !editing) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
            {onSuggestTasks && (
              <button onClick={() => onSuggestTasks(note)} title="Suggest tasks from this note"
                style={{ alignSelf: 'flex-start', border: 'none', background: 'none', color: B.tealDk, display: 'inline-flex', cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}><Icon name="sparkles" size={14} /></button>
            )}
            {canEdit && (
              <button onClick={() => setShowHistory(true)} title="Version history"
                style={{ alignSelf: 'flex-start', border: 'none', background: 'none', color: B.textLt, display: 'inline-flex', cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}><Icon name="undo" size={14} /></button>
            )}
            {canEdit && (
              <button onClick={() => onDelete(note.noteID)} title="Delete note"
                style={{ alignSelf: 'flex-start', border: 'none', background: 'none', color: B.textLt, display: 'inline-flex', cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}><Icon name="trash" size={14} /></button>
            )}
          </div>
        )}
      </div>

      {showHistory && (
        <NoteHistoryModal
          noteId={note.noteID}
          canRestore={canEdit}
          onRestore={(rev) => onEdit(note.noteID, { content: rev.content, noteDate: note.noteDate, mentionedUserIDs: [], attachments: [] })}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};

// ─── Notes feed ──────────────────────────────────────────────────────────────

export const NotesFeed = ({ taskId, dealId, dealDetailId, users, includeDealLevel = false, readOnly = false, showSummary = true, onChanged, targetNoteId = null, onNoteTargetHandled }) => {
  const { toast } = useToast();
  const params = taskId
    ? `taskId=${taskId}`
    : dealDetailId
      ? `dealId=${dealId}&dealDetailId=${dealDetailId}${includeDealLevel ? '&includeDealLevel=true' : ''}`
      : `dealId=${dealId}`;
  const { data: notes, loading, error, reload } = useApi(
    () => api.get(`notes?${params}`),
    [taskId, dealId, dealDetailId, includeDealLevel]
  );
  // The Claude summary of ALL notes in this scope, in chronological order. It is
  // the main surface of the tab; the raw list lives behind "Show All Notes".
  // The server never blocks on Claude — a stale/missing summary is regenerated
  // off the request path, so we poll until it's fresh.
  // When embedded in the combined "Notes & Tasks" panel (showSummary=false) the
  // parent owns a single notes+tasks summary, so we skip our own — but still
  // notify it (onChanged) whenever a note write should refresh that summary.
  const { data: summary, reload: reloadSummary } = useApi(
    () => (showSummary ? api.get(`notes/summary?${params}`) : Promise.resolve(null)),
    [taskId, dealId, dealDetailId, includeDealLevel, showSummary]
  );
  // A private, synced draft for this scope (only the creator's own). It's not a
  // note yet, so it never appears in the feed or the summary. `includeDealLevel`
  // is dropped server-side — a draft belongs to the exact scope its note will be
  // created in (matching `scope` in createNote below).
  const draftScope = taskId ? { taskId } : { dealId, dealDetailId: dealDetailId || null };
  const draftQs = taskId
    ? `taskId=${taskId}`
    : dealDetailId ? `dealId=${dealId}&dealDetailId=${dealDetailId}` : `dealId=${dealId}`;
  const { data: draft, reload: reloadDraft } = useApi(
    () => (readOnly ? Promise.resolve(null) : api.get(`note-drafts?${draftQs}`)),
    [taskId, dealId, dealDetailId, readOnly]
  );
  // AUTO-PROPOSED TASKS. Every note that lands in this scope — typed, dictated,
  // or captured from an email — is read for its action items off the request
  // path (api/src/functions/noteTaskProposals.js). What comes back are
  // PROPOSALS: nothing exists as a task until somebody presses Add below, which
  // goes through the ordinary create (Admin check, near-duplicate gate, audit).
  // A task's own notes have no such panel, and a read-only view proposes
  // nothing to act on — so neither asks.
  const proposalsEnabled = !readOnly && !taskId && !!dealId;
  const { data: proposalData, reload: reloadProposals } = useApi(
    () => (proposalsEnabled ? api.get(`note-task-proposals?${params}`) : Promise.resolve(null)),
    [taskId, dealId, dealDetailId, includeDealLevel, readOnly]
  );

  const [editorOpen, setEditorOpen] = useState(false);
  // A note this browser was writing here and never posted reopens by itself.
  const [resumeComposer, setResumeComposer] = useState(() => !readOnly && !!pendingComposerNoteId(composerScopeKey({ taskId, dealId, dealDetailId })));
  const [showAll, setShowAll] = useState(false);
  // Note id (from a global-search deep-link) to reveal, scroll to, and briefly
  // highlight once the feed has loaded.
  const [highlightNoteId, setHighlightNoteId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [promoteTask, setPromoteTask] = useState(null);
  const [promoteSaving, setPromoteSaving] = useState(false);
  // Creating a task — the post, the idempotency key and the near-duplicate
  // confirm — is shared with every other surface that creates one.
  const { runTaskCreate, duplicateDialog } = useTaskCreate();
  // AI "propose tasks from this note": { noteId, loading, tasks:[], addingIdx }
  const [suggestPanel, setSuggestPanel] = useState(null);
  // The auto-proposal panel's own local state. Accepting or dismissing hides the
  // item straight away (`${suggestionId}:${index}` is the key) rather than
  // waiting for a refetch, and the server is told separately — so a proposal
  // never flickers back after the click that resolved it.
  const [proposalHidden,    setProposalHidden]    = useState({});
  const [proposalEdits,     setProposalEdits]     = useState({});
  const [proposalsCollapsed, setProposalsCollapsed] = useState(false);
  // The proposal open in the draft editor — `{ p, item }`, or null.
  const [proposalEditing, setProposalEditing] = useState(null);
  // "Export PDF" — the scope's notes as one shareable document.
  const [exportOpen,        setExportOpen]        = useState(false);
  const [exporting,         setExporting]         = useState(false);
  const [exportSummary,     setExportSummary]     = useState(true);
  const [exportAttachments, setExportAttachments] = useState(true);

  const runExport = async () => {
    setExporting(true);
    try {
      const res = await exportNotesPdf({
        taskId, dealId, dealDetailId, includeDealLevel,
        includeSummary: exportSummary,
        includeAttachments: exportAttachments,
      });
      setExportOpen(false);
      if (res?.truncated) toast.info(`Only the most recent ${res.noteCount} notes fit in the PDF.`);
    } catch (e) { toast.error('Export failed: ' + e.message); }
    finally { setExporting(false); }
  };

  // Latest known draft id (from the fetch or a fresh autosave), used to finalize
  // on Post and to discard.
  const draftIdRef = useRef(null);
  useEffect(() => { if (draft?.draftId) draftIdRef.current = draft.draftId; }, [draft]);

  // Deep-link to a specific note (from global search): once the note is in the
  // loaded feed, expand the raw list, scroll the card into view, and highlight
  // it briefly. A ref guards against re-firing when `notes` reloads (attachment
  // polling) or when the parent clears the target — so no cleanup that would
  // cancel the scroll/highlight timers is returned here.
  const handledNoteTargetRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  useEffect(() => {
    if (!targetNoteId || !notes) return;
    if (handledNoteTargetRef.current === targetNoteId) return;
    const hit = notes.find(n => n.noteID === targetNoteId);
    if (!hit) return;                 // not in this scope's feed — leave for another panel
    handledNoteTargetRef.current = targetNoteId;
    setShowAll(true);
    setHighlightNoteId(targetNoteId);
    setTimeout(() => {                 // let the list expand first
      const el = document.getElementById(`note-${targetNoteId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    setTimeout(() => { if (mountedRef.current) setHighlightNoteId(null); }, 3200);
    onNoteTargetHandled?.();
  }, [targetNoteId, notes]); // eslint-disable-line react-hooks/exhaustive-deps

  // A just-uploaded document is summarized off the request path. Poll the feed
  // while any attachment's summary is still pending so it appears without a
  // manual reload. Bounded so it can't spin forever if it never resolves.
  const anyAttachmentPending = (notes || []).some(
    n => (n.attachments || []).some(a => a.aiSummaryStatus === 'pending')
  );
  useEffect(() => {
    if (!anyAttachmentPending) return undefined;
    let polls = 0;
    const t = setInterval(() => {
      if (++polls > 20) { clearInterval(t); return; }
      reload();
    }, 5000);
    return () => clearInterval(t);
  }, [anyAttachmentPending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while the summary is being (re)generated. Capped so a stuck/absent
  // worker (or missing ANTHROPIC_API_KEY) doesn't poll forever.
  const pollRef = useRef(0);
  useEffect(() => {
    const pending = summary && (summary.stale || summary.status === 'pending');
    if (!pending) { pollRef.current = 0; return undefined; }
    if (pollRef.current >= 24) return undefined;  // ~2 min at 5s intervals
    const t = setTimeout(() => { pollRef.current += 1; reloadSummary(); }, 5000);
    return () => clearTimeout(t);
  }, [summary, reloadSummary]);

  // Poll while a note is still being read for action items. Bounded, like the
  // summary poll — a missing worker or AI connection must not poll forever.
  const proposalPollRef = useRef(0);
  useEffect(() => {
    if (!proposalData?.pending) { proposalPollRef.current = 0; return undefined; }
    if (proposalPollRef.current >= 12) return undefined;   // ~1 min at 5s intervals
    const t = setTimeout(() => { proposalPollRef.current += 1; reloadProposals(); }, 5000);
    return () => clearTimeout(t);
  }, [proposalData, reloadProposals]);

  // Force a rebuild (server enqueues a fresh regeneration), then start polling.
  const refreshSummary = async () => {
    setRefreshing(true);
    try { await api.get(`notes/summary?${params}&refresh=1`); } catch { /* best-effort */ }
    pollRef.current = 0;
    reloadSummary();
    setRefreshing(false);
  };

  // ── Old server drafts ─────────────────────────────────────────────────────
  // Before the composer became the Notes-app editor, an unposted note was kept
  // in dbo.NoteDrafts. One still there seeds the composer once; posting
  // finalizes it server-side, and discarding drops it.
  const discardDraft = async () => {
    const id = draftIdRef.current || draft?.draftId;
    draftIdRef.current = null;
    try { if (id) await api.del(`note-drafts/${id}`); } catch { /* best-effort */ }
    reloadDraft();
  };

  // The feed arrives sorted by effective day (NoteDate, else filed date), so a
  // simple run-length grouping keeps each meeting's notes together.
  const dayGroups = useMemo(() => {
    const groups = [];
    for (const n of notes || []) {
      const day = (n.noteDate || n.createdUTC || '').slice(0, 10);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.notes.push(n);
      else groups.push({ day, notes: [n] });
    }
    return groups;
  }, [notes]);

  // After the composer posts: the note is now on this record.
  const afterPost = () => {
    reload();
    reloadSummary();
    reloadDraft();
    onChanged?.();
  };

  const editNote = async (noteId, data) => {
    const res = await api.put(`notes/${noteId}`, data);
    if (res?.attachmentError) toast.error(res.attachmentError);
    reload();
    reloadSummary();
    onChanged?.();
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await api.del(`notes/${noteId}`);
    reload();
    reloadSummary();
    onChanged?.();
  };

  // Idempotency key for promote-to-task — stable across retries of one promote
  // (rotated on success / when a new promote dialog opens) so a timeout retry
  // dedupes instead of creating a second task from the same note.
  const promoteReqId = useRef(null);


  const handlePromoteToTask = async () => {
    if (!promoteTask?.title?.trim()) return;
    if (!promoteReqId.current) promoteReqId.current = (window.crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setPromoteSaving(true);
    try {
      await runTaskCreate({
        title: promoteTask.title.trim(),
        priority: 'Medium',
        status: 'Not Started',
        dealId: dealId || null,
        dealDetailId: dealDetailId || null,
        sourceNoteId: promoteTask.noteId,
        clientRequestId: promoteReqId.current,
      }, () => {
        promoteReqId.current = null;
        setPromoteTask(null);
        notifyTasksChanged();
        onChanged?.();
      });
    } catch (e) { toast.error(e.message); }
    finally { setPromoteSaving(false); }
  };

  // AI: read one note and propose the action items in it as tasks. The user
  // reviews and adds the ones they want (each add routes through runTaskCreate
  // so the near-duplicate gate still applies). Proposals are advisory — nothing
  // is created until the user clicks "Add".
  const handleSuggestTasks = async (note) => {
    setSuggestPanel({ noteId: note.noteID, loading: true, tasks: [], addingIdx: null });
    try {
      const res = await api.post('notes/suggest-tasks', {
        noteId: note.noteID,
        dealId: dealId || null,
        dealDetailId: dealDetailId || null,
      });
      const tasks = (res?.tasks || []).map(t => ({ ...t, reqId: window.crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}` }));
      if (!tasks.length) { toast.info('No action items found in this note.'); setSuggestPanel(null); return; }
      setSuggestPanel({ noteId: note.noteID, loading: false, tasks, allowTasks: res?.allowTasks !== false, addingIdx: null });
    } catch (e) {
      toast.error(e.message || 'Could not suggest tasks.');
      setSuggestPanel(null);
    }
  };

  const addSuggestedTask = async (idx) => {
    const panel = suggestPanel;
    const t = panel?.tasks?.[idx];
    if (!t?.title?.trim()) return;
    setSuggestPanel(p => ({ ...p, addingIdx: idx }));
    try {
      await runTaskCreate({
        title: t.title.trim(),
        description: t.description || null,
        priority: t.priority || 'Medium',
        status: 'Not Started',
        dueDate: t.dueDate || null,
        assigneeIds: t.assigneeIds || [],
        dealId: dealId || null,
        dealDetailId: dealDetailId || null,
        sourceNoteId: panel.noteId,
        clientRequestId: t.reqId,
      }, () => {
        setSuggestPanel(p => {
          if (!p) return p;
          const remaining = p.tasks.filter((_, i) => i !== idx);
          return remaining.length ? { ...p, tasks: remaining, addingIdx: null } : null;
        });
        toast.success('Task created.');
        notifyTasksChanged();
        onChanged?.();
      });
    } catch (e) { toast.error(e.message); }
    finally { setSuggestPanel(p => (p ? { ...p, addingIdx: null } : p)); }
  };

  // ── Auto-proposed tasks ───────────────────────────────────────────────────
  const proposalKey = (p, item) => `${p.suggestionId}:${item.index}`;
  // One idempotency key per proposal, held for the life of the panel: a retried
  // add (a timeout, a duplicate confirm) dedupes against the first attempt
  // instead of creating a second task. It must be a UUID — normalizeRequestId
  // discards anything else, so the proposal key itself cannot stand in.
  const proposalReqIds = useRef({});
  const proposalReqId = (key) => {
    if (!proposalReqIds.current[key]) {
      proposalReqIds.current[key] = window.crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    return proposalReqIds.current[key];
  };
  const proposalGroups = (proposalData?.proposals || [])
    .map(p => ({ ...p, items: (p.items || []).filter(it => !proposalHidden[proposalKey(p, it)]) }))
    .filter(p => p.items.length > 0);

  // Tell the server the proposal was dealt with. The item is already hidden, so
  // this is bookkeeping — a failure is worth a toast and nothing more, and the
  // proposal simply reappears on the next load.
  const recordProposal = async (p, item, action, taskId2) => {
    try {
      await api.post(`note-task-proposals/${p.suggestionId}/resolve`, {
        index: item.index, action, taskId: taskId2 || null,
      });
    } catch (e) {
      setProposalHidden(h => { const next = { ...h }; delete next[proposalKey(p, item)]; return next; });
      toast.error(e.message || 'Could not update the suggestion.');
    }
  };

  const dismissProposal = (p, item) => {
    setProposalHidden(h => ({ ...h, [proposalKey(p, item)]: true }));
    recordProposal(p, item, 'dismissed');
  };

  // ADD OPENS THE DRAFT; IT DOES NOT CREATE. Same as the global pop-up — the
  // wording, the assignee and the due date are worth fixing before the row
  // exists rather than in the task list afterwards. The editor does the
  // ordinary POST /api/tasks, so the near-duplicate gate still catches "we
  // already have this task", which is exactly what an unprompted reader needs.
  const addProposedTask = (p, item) => setProposalEditing({ p, item });

  const proposalSaved = async (p, item, created) => {
    const key = proposalKey(p, item);
    setProposalEditing(null);
    setProposalHidden(h => ({ ...h, [key]: true }));
    await recordProposal(p, item, 'accepted', created?.taskID || created?.TaskID || null);
    toast.success('Task created.');
    notifyTasksChanged();
    onChanged?.();
  };

  return (
    <div style={{ marginTop: 8 }}>
      <style>{`
        /* Legacy HTML note styles */
        .note-content h1 { font-size: 16px; font-weight: 700; color: #234c5a; margin: 14px 0 4px 0; line-height: 1.3; }
        .note-content p { margin: 6px 0; }
        .note-content h2 { font-size: 15px; font-weight: 700; color: #234c5a; margin: 14px 0 4px 0; line-height: 1.3; border-bottom: 1px solid #dde8e5; padding-bottom: 4px; }
        .note-content h3 { font-size: 13px; font-weight: 700; color: #4a6572; margin: 10px 0 2px 0; line-height: 1.3; text-transform: uppercase; letter-spacing: 0.5px; }
        .note-content ul, .note-content ol { margin: 4px 0; padding-left: 22px; }
        /* Task summary: four fixed sections, each a small label over its text */
        .task-summary h3 { font-size: 10px; color: #4a6572; margin: 12px 0 2px 0; padding-top: 10px; border-top: 1px solid #eef2f1; }
        .task-summary h3:first-child { margin-top: 0; padding-top: 0; border-top: none; }
        .task-summary p { margin: 2px 0; }
        .task-summary ul { margin: 2px 0; }
        .note-content a { color: #2563eb; text-decoration: underline; }
        .note-content code { background: #f1f5f9; border-radius: 3px; padding: 1px 5px; font-size: 11px; font-family: monospace; }
        .note-content mark { border-radius: 2px; padding: 1px 0; }
        /* Legacy checklist */
        .note-check-item { display: flex; align-items: flex-start; gap: 6px; padding: 2px 0; }
        .note-checkbox { display: inline-flex; align-items: center; justify-content: center; width: 14px; min-width: 14px; height: 14px; border: 2px solid #dde8e5; border-radius: 3px; cursor: pointer; margin-top: 2px; }
        .note-checkbox[data-checked="true"] { background: #1e7d5a; border-color: #1e7d5a; }
        .note-checkbox[data-checked="true"]::after { content: "✓"; color: white; font-size: 9px; font-weight: 700; }
        /* BlockNote layout */
        .bn-editor { padding: 8px 12px !important; font-family: inherit !important; font-size: 13px !important; min-height: 80px; }
        .bn-block-outer { width: 100% !important; }
        /* Ensure BlockNote floating menus (formatting toolbar, color picker) appear above app chrome.
           The "/" slash menu is not a mantine dropdown — its z-index is raised via
           SuggestionMenuController's floatingUIOptions.elementProps above. */
        .mantine-Popover-dropdown, .mantine-Menu-dropdown, .mantine-Tooltip-tooltip { z-index: 9999 !important; }
      `}</style>

      {!readOnly && ((editorOpen || !!draft?.content || resumeComposer) ? (
        <DealNoteComposer
          scope={draftScope}
          dealId={dealId}
          dealDetailId={dealDetailId}
          users={users}
          legacyDraft={draft?.content ? draft : null}
          onPosted={() => { setEditorOpen(false); setResumeComposer(false); afterPost(); }}
          onClose={() => { setEditorOpen(false); setResumeComposer(false); if (draft?.draftId) discardDraft(); }}
        />
      ) : (
        <button
          onClick={() => setEditorOpen(true)}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '9px 14px', marginBottom: 16,
            border: `1px solid ${B.border}`, borderRadius: 8,
            background: B.white, color: B.textLt,
            fontSize: 13, cursor: 'text', fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        >
          Write a note…
        </button>
      ))}

      {promoteTask && (
        <div style={{ background: B.tealLt, border: `1px solid ${B.tealDk}`, borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: B.navyDk, marginBottom: 8 }}>Create Task from Note</div>
          <input autoFocus value={promoteTask.title}
            onChange={e => setPromoteTask(p => ({ ...p, title: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') handlePromoteToTask(); if (e.key === 'Escape') setPromoteTask(null); }}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '7px 10px', border: `1px solid ${B.border}`, borderRadius: 6, fontFamily: 'inherit', outline: 'none', marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" size="sm" onClick={() => setPromoteTask(null)}>Cancel</Btn>
            <Btn size="sm" disabled={!promoteTask.title?.trim() || promoteSaving} onClick={handlePromoteToTask}>
              {promoteSaving ? 'Creating…' : 'Create Task'}
            </Btn>
          </div>
        </div>
      )}

      {suggestPanel && (
        <div style={{ background: B.tealLt, border: `1px solid ${B.tealDk}`, borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: B.navyDk }}>
              <Icon name="sparkles" size={13} /> AI-suggested tasks from this note
            </span>
            <button onClick={() => setSuggestPanel(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: B.textMd, display: 'inline-flex', padding: 0 }} aria-label="Close"><Icon name="close" size={15} /></button>
          </div>
          {suggestPanel.loading ? (
            <div style={{ fontSize: 12, color: B.textMd }}>Reading the note for action items…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {suggestPanel.tasks.map((t, i) => (
                <div key={i} style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 6, padding: '8px 10px' }}>
                  <input value={t.title}
                    onChange={e => setSuggestPanel(p => ({ ...p, tasks: p.tasks.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) }))}
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, fontWeight: 600, padding: '5px 8px', border: `1px solid ${B.border}`, borderRadius: 5, fontFamily: 'inherit', outline: 'none', marginBottom: 6 }} />
                  {t.sourceExcerpt && (
                    <div style={{ fontSize: 11, color: B.textLt, fontStyle: 'italic', marginBottom: 6, borderLeft: `2px solid ${B.border}`, paddingLeft: 6 }}>“{t.sourceExcerpt}”</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {t.priority && <span style={{ fontSize: 10, fontWeight: 700, color: B.textMd, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 10, padding: '1px 8px' }}>{t.priority}</span>}
                    {t.dueDate && <span style={{ fontSize: 11, color: B.textMd }}>Due {String(t.dueDate).slice(0, 10)}</span>}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <Btn variant="ghost" size="sm" onClick={() => setSuggestPanel(p => { const rem = p.tasks.filter((_, j) => j !== i); return rem.length ? { ...p, tasks: rem } : null; })}>Dismiss</Btn>
                      {suggestPanel.allowTasks !== false && (
                        <Btn size="sm" disabled={!t.title?.trim() || suggestPanel.addingIdx === i} onClick={() => addSuggestedTask(i)}>
                          {suggestPanel.addingIdx === i ? 'Adding…' : 'Add task'}
                        </Btn>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {suggestPanel.allowTasks === false && (
                <div style={{ fontSize: 11, color: B.textLt }}>Only admins can create tasks — these are for reference.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AUTO-PROPOSED TASKS — the unprompted half of "suggest tasks". Every note
          that lands here is read for action items off the request path, and an
          EMAIL captured onto the deal is the case this exists for: it arrives
          with nobody watching, so its action items would otherwise be found only
          by somebody re-reading the note. These are proposals — the task exists
          when you press Add, which is an ordinary create. */}
      {proposalsEnabled && (proposalGroups.length > 0 || (proposalData?.pending > 0)) && (
        <div style={{ background: B.tealLt, border: `1px solid ${B.tealDk}`, borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: B.navyDk }}>
              <Icon name="sparkles" size={13} /> Possible tasks from recent notes
              {proposalGroups.length > 0 && (
                <span style={{ fontWeight: 600, color: B.textMd }}>
                  ({proposalGroups.reduce((n, g) => n + g.items.length, 0)})
                </span>
              )}
            </span>
            {proposalGroups.length > 0 && (
              <button onClick={() => setProposalsCollapsed(v => !v)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: B.textMd, fontSize: 11, fontWeight: 600, padding: 0 }}>
                {proposalsCollapsed ? 'Show' : 'Hide'}
              </button>
            )}
          </div>

          {proposalGroups.length === 0 ? (
            <div style={{ fontSize: 12, color: B.textMd, marginTop: 6 }}>Reading the latest note for action items…</div>
          ) : !proposalsCollapsed && (
            <>
              <div style={{ fontSize: 11, color: B.textMd, margin: '6px 0 8px' }}>
                Nothing is created until you add it.
                {proposalData?.pending > 0 && ' Still reading the newest notes…'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {proposalGroups.map(p => (
                  <div key={p.suggestionId}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11, color: B.textMd, marginBottom: 4 }}>
                      {p.sourceType === 'email' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700, color: B.navyDk }}>
                          <Icon name="emailInbox" size={12} /> From an email
                        </span>
                      )}
                      <span style={{ fontWeight: 600 }}>
                        {p.sourceType === 'email'
                          ? (p.emailSubject || p.noteAuthor || 'Captured email')
                          : `Note by ${p.noteAuthor || 'someone'}`}
                      </span>
                      {p.noteDate && <span>· {fmtNoteDate(p.noteDate)}</span>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {p.items.map(item => {
                        const key  = proposalKey(p, item);
                        const title = proposalEdits[key] ?? item.title;
                        return (
                          <div key={key} style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 6, padding: '8px 10px' }}>
                            <input value={title}
                              onChange={e => setProposalEdits(m => ({ ...m, [key]: e.target.value }))}
                              style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, fontWeight: 600, padding: '5px 8px', border: `1px solid ${B.border}`, borderRadius: 5, fontFamily: 'inherit', outline: 'none', marginBottom: 6 }} />
                            {item.sourceExcerpt && (
                              <div style={{ fontSize: 11, color: B.textLt, fontStyle: 'italic', marginBottom: 6, borderLeft: `2px solid ${B.border}`, paddingLeft: 6 }}>“{item.sourceExcerpt}”</div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              {item.priority && <span style={{ fontSize: 10, fontWeight: 700, color: B.textMd, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 10, padding: '1px 8px' }}>{item.priority}</span>}
                              {item.dueDate && <span style={{ fontSize: 11, color: B.textMd }}>Due {String(item.dueDate).slice(0, 10)}</span>}
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                                <Btn variant="ghost" size="sm" onClick={() => dismissProposal(p, item)}>Dismiss</Btn>
                                {proposalData?.allowTasks !== false && (
                                  <Btn size="sm" disabled={!String(title || '').trim()} onClick={() => addProposedTask(p, item)}>
                                    Add task…
                                  </Btn>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {proposalData?.allowTasks === false && (
                <div style={{ fontSize: 11, color: B.textLt, marginTop: 8 }}>Only admins can create tasks — these are for reference.</div>
              )}
            </>
          )}
        </div>
      )}

      {proposalEditing && (
        <TaskDraftModal
          users={users}
          draft={{
            title: proposalEdits[proposalKey(proposalEditing.p, proposalEditing.item)] ?? proposalEditing.item.title,
            description: proposalEditing.item.description,
            dueDate: proposalEditing.item.dueDate,
            priority: proposalEditing.item.priority,
            assigneeIds: proposalEditing.item.assigneeIds,
            // The task lands where the NOTE is, not where the panel is: a
            // deal-level note read in a detail's merged feed belongs to the deal.
            dealId: proposalEditing.p.dealId || dealId || null,
            dealDetailId: proposalEditing.p.dealDetailId || null,
            sourceNoteId: proposalEditing.p.noteId,
            sourceExcerpt: proposalEditing.item.sourceExcerpt,
            clientRequestId: proposalReqId(proposalKey(proposalEditing.p, proposalEditing.item)),
          }}
          onCancel={() => setProposalEditing(null)}
          onSaved={(created) => proposalSaved(proposalEditing.p, proposalEditing.item, created)}
        />
      )}

      {duplicateDialog}

      {loading && (notes || []).length === 0 && <Spinner />}
      {error && (
        <div style={{ textAlign: 'center', color: '#c0392b', padding: '16px 0', fontSize: 13 }}>
          Failed to load notes: {error}{' '}
          <button onClick={reload} style={{ marginLeft: 8, cursor: 'pointer', textDecoration: 'underline', background: 'none', border: 'none', color: '#c0392b', fontSize: 13 }}>Retry</button>
        </div>
      )}

      {/* Summary — the main surface. A running Claude digest of every note in
          this scope, in chronological order. Regenerated off the request path;
          shows "Updating…" and polls while a rebuild is in flight. Suppressed
          when the combined Notes & Tasks panel owns a shared summary. */}
      {showSummary && !error && (notes || []).length > 0 && (() => {
        const updating = !!summary && (summary.stale || summary.status === 'pending');
        return (
          <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, background: B.white, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5 }}>Summary</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {updating ? (
                  <span style={{ fontSize: 11, color: B.textLt, fontStyle: 'italic' }}>Updating…</span>
                ) : summary?.generatedUTC ? (
                  <span style={{ fontSize: 11, color: B.textLt }}>Updated {fmtNoteDate(summary.generatedUTC)}</span>
                ) : null}
                {!readOnly && (
                  <button
                    onClick={refreshSummary}
                    disabled={refreshing || updating}
                    title="Refresh summary"
                    aria-label="Regenerate summary"
                    style={{ background: 'none', border: 'none', cursor: (refreshing || updating) ? 'default' : 'pointer', color: B.textMd, lineHeight: 1, padding: 2, opacity: (refreshing || updating) ? 0.5 : 1, display: 'inline-flex' }}
                  ><Icon name="refresh" size={14} /></button>
                )}
              </div>
            </div>
            {summary?.content ? (
              // A task's summary is always the four fixed sections (Status /
              // Done so far / Blocker / Next step, api/src/noteSummarizer.js);
              // .task-summary lays them out as labelled blocks.
              <div className={taskId ? 'note-content task-summary' : 'note-content'} style={{ fontSize: 13, color: B.text, lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: summary.content }} />
            ) : summary?.status === 'error' ? (
              <div style={{ fontSize: 12, color: B.textLt }}>
                Summary couldn&apos;t be generated.{!readOnly && ' Use the refresh button to try again.'}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: B.textLt, fontStyle: 'italic' }}>Generating summary…</div>
            )}
          </div>
        );
      })()}

      {/* The raw chronological list moves behind a toggle, alongside "Export
          PDF" — the one place a whole scope's notes can leave the app as a
          shareable document. */}
      {!error && (notes || []).length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: showAll ? 8 : 0 }}>
          <button
            onClick={() => setShowAll(v => !v)}
            style={{
              flex: 1, textAlign: 'center',
              padding: '8px 14px',
              border: `1px solid ${B.border}`, borderRadius: 8,
              background: B.white, color: B.textMd,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          >
            {showAll ? 'Hide notes' : `Show All Notes (${(notes || []).length})`}
          </button>
          <button
            onClick={() => setExportOpen(true)}
            disabled={exporting}
            title="Export these notes to a PDF you can share"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '8px 14px',
              border: `1px solid ${B.border}`, borderRadius: 8,
              background: B.white, color: exporting ? B.textLt : B.navy,
              fontSize: 12, fontWeight: 600, cursor: exporting ? 'default' : 'pointer',
              fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            <Icon name="download" size={13} /> {exporting ? 'Building…' : 'Export PDF'}
          </button>
        </div>
      )}

      {exportOpen && (
        <Modal title="Export notes to PDF" width={440} onClose={() => setExportOpen(false)}>
          <div style={{ fontSize: 13, color: B.textMd, marginBottom: 14, lineHeight: 1.5 }}>
            {`${(notes || []).length} note${(notes || []).length === 1 ? '' : 's'} in this `
              + `${taskId ? 'task' : dealDetailId ? 'deal detail' : 'deal'} will be written to a single `
              + `PDF, oldest first, with each note’s author and date.`}
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: B.text, marginBottom: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={exportSummary} onChange={e => setExportSummary(e.target.checked)} style={{ marginTop: 2 }} />
            <span>Start with the AI summary<div style={{ fontSize: 11, color: B.textLt }}>A one-page overview ahead of the individual notes.</div></span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: B.text, marginBottom: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={exportAttachments} onChange={e => setExportAttachments(e.target.checked)} style={{ marginTop: 2 }} />
            <span>List each note&apos;s attachments<div style={{ fontSize: 11, color: B.textLt }}>File names only — the files themselves aren&apos;t embedded.</div></span>
          </label>
          <div style={{ fontSize: 11, color: B.textLt, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 6, padding: '8px 10px', margin: '14px 0 4px' }}>
            These are internal notes. Check what&apos;s in them before sending the PDF outside Harvus.
          </div>
          <ModalFooter>
            <Btn variant="ghost" onClick={() => setExportOpen(false)}>Cancel</Btn>
            <Btn disabled={exporting} onClick={runExport}>{exporting ? 'Building…' : 'Download PDF'}</Btn>
          </ModalFooter>
        </Modal>
      )}

      {showAll && !error && dayGroups.map(group => (
        <div key={group.day}>
          {group.notes.length > 1 && (
            <div style={{ paddingTop: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {fmtUserDate(group.day)} · {group.notes.length} notes
              </span>
            </div>
          )}
          {group.notes.map(note => (
            <NoteCard key={note.noteID} note={note} users={users} onEdit={editNote} onDelete={deleteNote} readOnly={readOnly}
              highlighted={note.noteID === highlightNoteId}
              onSuggestTasks={readOnly || taskId ? null : handleSuggestTasks}
              onPromoteToTask={readOnly || taskId ? null : item => { promoteReqId.current = null; setPromoteTask({ text: item.text, noteId: item.noteId, title: item.text }); }} />
          ))}
        </div>
      ))}

      {!loading && !error && (notes || []).length === 0 && (
        <div style={{ textAlign: 'center', color: B.textLt, padding: '32px 0', fontSize: 13 }}>
          {readOnly ? 'No notes on this deal yet.' : 'No notes yet. Add the first note above.'}
        </div>
      )}
    </div>
  );
};
